import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Product } from "@/entities/Product";
import { Project } from "@/entities/Project";
import { Offering } from "@/entities/Offering";
import {
  ChevronLeft, ChevronRight, Star, MapPin, Instagram, Twitter,
  Youtube, ExternalLink, Package, Briefcase, Sparkles,
  TrendingUp, CheckCircle, Clock, ArrowRight
} from "lucide-react";

function getYouTubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let videoId = null;
    if (u.hostname.includes("youtu.be")) videoId = u.pathname.slice(1);
    else if (u.hostname.includes("youtube.com")) videoId = u.searchParams.get("v");
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
  } catch { return null; }
}

function computeScore(creator, products, projects, offerings) {
  const salesScore = Math.min((creator.total_sales || 0) / 2, 30);
  const projectScore = Math.min(projects.filter(p => p.status === "completed" || p.status === "featured").length * 5, 25);
  const productScore = Math.min(products.length * 2, 20);
  const offeringScore = Math.min(offerings.length * 3, 15);
  const profileScore = creator.youtube_video_url ? 5 : 0;
  const bioScore = creator.bio?.length > 100 ? 5 : 2;
  return Math.round(Math.min(salesScore + projectScore + productScore + offeringScore + profileScore + bioScore, 100));
}

function ScoreBadge({ score }) {
  const tier = score >= 80 ? { label: "Elite", color: "bg-amber-500" }
    : score >= 60 ? { label: "Pro", color: "bg-purple-500" }
    : score >= 40 ? { label: "Rising", color: "bg-blue-500" }
    : { label: "New", color: "bg-gray-400" };
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-12 h-12">
        <svg className="w-12 h-12 -rotate-90" viewBox="0 0 56 56">
          <circle cx="28" cy="28" r="24" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
          <circle cx="28" cy="28" r="24" fill="none" stroke="#c8a96e" strokeWidth="4"
            strokeDasharray={`${(score / 100) * 150.8} 150.8`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white">{score}</span>
      </div>
      <div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full text-white ${tier.color}`}>{tier.label}</span>
        <p className="text-[10px] text-white/50 mt-0.5">Creator Score</p>
      </div>
    </div>
  );
}

function MiniProjectCard({ project }) {
  return (
    <div className="bg-white/10 border border-white/10 rounded-xl overflow-hidden flex-shrink-0 w-52">
      {project.images?.[0] && (
        <div className="h-28 overflow-hidden">
          <img src={project.images[0]} alt={project.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-3">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${project.status === "featured" ? "bg-[#c8a96e] text-white" : project.status === "completed" ? "bg-green-500/20 text-green-300" : "bg-blue-500/20 text-blue-300"}`}>
          {project.status === "featured" ? "★ Featured" : project.status === "completed" ? "Completed" : "In Progress"}
        </span>
        <p className="text-white text-xs font-semibold mt-1.5 line-clamp-1">{project.title}</p>
        <p className="text-white/50 text-[10px] line-clamp-2 mt-0.5">{project.description}</p>
      </div>
    </div>
  );
}

function MiniProductCard({ product }) {
  return (
    <Link to={createPageUrl(`ProductDetail?id=${product.id}`)} className="bg-white/10 border border-white/10 rounded-xl overflow-hidden flex-shrink-0 w-44 hover:bg-white/20 transition-colors">
      <div className="h-28 overflow-hidden bg-white/5">
        {product.images?.[0] && <img src={product.images[0]} alt={product.name} className="w-full h-full object-cover" />}
      </div>
      <div className="p-3">
        <p className="text-white text-xs font-semibold line-clamp-1">{product.name}</p>
        <p className="text-[#c8a96e] text-xs font-bold mt-0.5">${product.price}</p>
      </div>
    </Link>
  );
}

function MiniOfferingCard({ offering }) {
  const priceLabel = offering.price_type === "free" ? "Free"
    : offering.price_type === "contact" ? "Contact"
    : offering.price_type === "starting_at" ? `From $${offering.price}`
    : `$${offering.price}`;
  return (
    <div className="bg-white/10 border border-white/10 rounded-xl overflow-hidden flex-shrink-0 w-52">
      {offering.images?.[0] && (
        <div className="h-28 overflow-hidden">
          <img src={offering.images[0]} alt={offering.title} className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-3">
        <span className="text-[9px] font-bold bg-[#c8a96e]/20 text-[#c8a96e] px-1.5 py-0.5 rounded-full">{offering.type}</span>
        <p className="text-white text-xs font-semibold mt-1.5 line-clamp-1">{offering.title}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-[#c8a96e] text-xs font-bold">{priceLabel}</span>
          {offering.duration && <span className="text-white/40 text-[9px] flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{offering.duration}</span>}
        </div>
      </div>
    </div>
  );
}

// Per-creator data loader + slide renderer
function CreatorSlide({ creator, isActive }) {
  const [products, setProducts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [offerings, setOfferings] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("projects");

  useEffect(() => {
    if (!isActive || loaded) return;
    Promise.all([
      Product.filter({ creator_id: creator.id, status: "active" }, "-createdAt", 12),
      Project.filter({ creator_id: creator.id }, "-createdAt", 12),
      Offering.filter({ creator_id: creator.id, status: "active" }, "-createdAt", 12),
    ]).then(([prods, projs, offs]) => {
      setProducts(prods); setProjects(projs); setOfferings(offs);
      setLoaded(true);
    });
  }, [isActive, creator.id]);

  const score = loaded ? computeScore(creator, products, projects, offerings) : 0;
  const completedProjects = projects.filter(p => p.status === "completed" || p.status === "featured").length;
  const embedUrl = getYouTubeEmbedUrl(creator.youtube_video_url);

  const tabs = [
    { id: "projects", label: "Projects", icon: <Briefcase className="w-3.5 h-3.5" />, count: projects.length, items: projects },
    { id: "products", label: "Products", icon: <Package className="w-3.5 h-3.5" />, count: products.length, items: products },
    { id: "offerings", label: "Offerings", icon: <Sparkles className="w-3.5 h-3.5" />, count: offerings.length, items: offerings },
  ];

  const activeItems = tabs.find(t => t.id === activeTab)?.items || [];

  return (
    <div className="w-full bg-[#1a1a1a] rounded-2xl overflow-hidden">
      {/* ── Video / Banner Hero ── */}
      <div className="relative">
        {embedUrl ? (
          <div className="relative w-full" style={{ paddingTop: "42%" }}>
            <iframe
              src={embedUrl}
              title={`${creator.name} intro`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        ) : (
          <div className="relative h-56 md:h-72 overflow-hidden">
            <img
              src={creator.banner_image || "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1400&h=500&fit=crop"}
              alt={creator.name}
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a] to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-white/40">
                <Youtube className="w-10 h-10 mx-auto mb-1" />
                <p className="text-xs">Video intro coming soon</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Profile Header ── */}
      <div className="px-5 md:px-8 pt-5 pb-4">
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <img
            src={creator.avatar || "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=200&h=200&fit=crop"}
            alt={creator.name}
            className="w-16 h-16 rounded-2xl object-cover border-2 border-white/20 flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
              <div>
                <h3 className="font-display text-xl md:text-2xl font-bold text-white">{creator.name}</h3>
                <p className="text-[#c8a96e] text-sm font-medium">{creator.primary_category || creator.specialty}</p>
              </div>
              {loaded && <ScoreBadge score={score} />}
            </div>
            {creator.location && (
              <p className="text-xs text-white/40 flex items-center gap-1 mb-2"><MapPin className="w-3 h-3" />{creator.location}</p>
            )}
            <p className="text-sm text-white/70 leading-relaxed line-clamp-2">{creator.bio}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              {creator.social_instagram && (
                <a href={`https://instagram.com/${creator.social_instagram.replace("@","")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-white/40 hover:text-[#c8a96e]">
                  <Instagram className="w-3 h-3" />{creator.social_instagram}
                </a>
              )}
              {creator.social_twitter && (
                <a href={`https://twitter.com/${creator.social_twitter.replace("@","")}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-white/40 hover:text-[#c8a96e]">
                  <Twitter className="w-3 h-3" />{creator.social_twitter}
                </a>
              )}
              {creator.youtube_video_url && (
                <a href={creator.youtube_video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-red-400 hover:underline">
                  <Youtube className="w-3 h-3" /> YouTube
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Metric Stats */}
        <div className="grid grid-cols-4 gap-2 mt-4 pt-4 border-t border-white/10">
          {[
            { icon: <TrendingUp className="w-3.5 h-3.5 text-[#c8a96e]" />, value: creator.total_sales || 0, label: "Sales" },
            { icon: <CheckCircle className="w-3.5 h-3.5 text-green-400" />, value: completedProjects, label: "Projects" },
            { icon: <Package className="w-3.5 h-3.5 text-blue-400" />, value: products.length, label: "Products" },
            { icon: <Sparkles className="w-3.5 h-3.5 text-purple-400" />, value: offerings.length, label: "Offerings" },
          ].map((s, i) => (
            <div key={i} className="bg-white/5 rounded-xl p-2.5 text-center">
              <div className="flex justify-center mb-1">{s.icon}</div>
              <div className="font-display text-lg font-bold text-white">{loaded ? s.value : "—"}</div>
              <div className="text-[9px] text-white/40 uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="px-5 md:px-8">
        <div className="flex gap-1 bg-white/5 rounded-xl p-1 mb-4">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${
                activeTab === tab.id ? "bg-white text-[#1a1a1a] shadow-sm" : "text-white/50 hover:text-white"
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold ${activeTab === tab.id ? "bg-[#1a1a1a]/10 text-[#1a1a1a]" : "bg-white/10 text-white/40"}`}>
                {loaded ? tab.count : "…"}
              </span>
            </button>
          ))}
        </div>

        {/* Tab content — horizontal scroll row */}
        <div className="pb-6">
          {!loaded ? (
            <div className="flex gap-3 overflow-x-auto pb-2">
              {[...Array(3)].map((_, i) => <div key={i} className="flex-shrink-0 w-44 h-36 bg-white/5 rounded-xl animate-pulse" />)}
            </div>
          ) : activeItems.length === 0 ? (
            <p className="text-white/30 text-xs text-center py-6">Nothing here yet.</p>
          ) : (
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
              {activeTab === "projects" && activeItems.map(p => <MiniProjectCard key={p.id} project={p} />)}
              {activeTab === "products" && activeItems.map(p => <MiniProductCard key={p.id} product={p} />)}
              {activeTab === "offerings" && activeItems.map(o => <MiniOfferingCard key={o.id} offering={o} />)}
            </div>
          )}
        </div>
      </div>

      {/* ── View Full Profile CTA ── */}
      <div className="px-5 md:px-8 pb-6">
        <Link
          to={createPageUrl(`CreatorProfile?id=${creator.id}`)}
          className="w-full flex items-center justify-center gap-2 bg-[#c8a96e] hover:bg-[#a8893e] text-white font-semibold py-3 rounded-full transition-colors text-sm"
        >
          View Full Profile <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default function FeaturedCreatorCarousel({ creators, loading }) {
  const [index, setIndex] = useState(0);

  const prev = useCallback(() => setIndex(i => (i - 1 + creators.length) % creators.length), [creators.length]);
  const next = useCallback(() => setIndex(i => (i + 1) % creators.length), [creators.length]);

  // Reset index if creators change
  useEffect(() => { setIndex(0); }, [creators.length]);

  if (loading) {
    return (
      <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden animate-pulse">
        <div className="h-64 bg-white/5" />
        <div className="p-6 space-y-3">
          <div className="h-5 bg-white/10 rounded w-1/3" />
          <div className="h-3 bg-white/10 rounded w-1/2" />
          <div className="grid grid-cols-4 gap-2 mt-4">
            {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white/5 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!creators.length) return (
    <div className="bg-[#1a1a1a] rounded-2xl p-12 text-center text-white/30 text-sm">No featured creators yet.</div>
  );

  return (
    <div className="relative">
      {/* Slide */}
      <CreatorSlide key={creators[index].id} creator={creators[index]} isActive={true} />

      {/* Navigation arrows */}
      {creators.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/3 -translate-y-1/2 z-10 w-9 h-9 bg-black/60 hover:bg-[#c8a96e] text-white rounded-full flex items-center justify-center transition-colors backdrop-blur-sm shadow-lg"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/3 -translate-y-1/2 z-10 w-9 h-9 bg-black/60 hover:bg-[#c8a96e] text-white rounded-full flex items-center justify-center transition-colors backdrop-blur-sm shadow-lg"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {creators.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {creators.map((c, i) => (
            <button
              key={c.id}
              onClick={() => setIndex(i)}
              className={`transition-all rounded-full ${i === index ? "w-6 h-2 bg-[#c8a96e]" : "w-2 h-2 bg-gray-600 hover:bg-gray-400"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}